import React, { useEffect, useState, useRef } from "react";
import { client } from "../../api/client";
import LoadingSpinner from "../../components/ui/LoadingSpinner";

interface Props {
  showConfirm: (config: {
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    cancelLabel?: string;
    variant?: "primary" | "danger" | "warning";
    onConfirm: () => void;
    onCancel?: () => void;
  }) => void;
  showInfo: (cfg: any) => void;
}

interface ImportJob {
  id: number;
  target_model: string;
  filename: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  status: string;
  error_message?: string;
  created_at: string;
}

const MyUploadsTab: React.FC<Props> = ({ showConfirm, showInfo }) => {
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  const [selectedJob, setSelectedJob] = useState<ImportJob | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [missingSummary, setMissingSummary] = useState<Record<string, number>>(
    {},
  );
  const [showOnlyErrors, setShowOnlyErrors] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobsCount, setTotalJobsCount] = useState(0);

  const [rowsPage, setRowsPage] = useState(1);
  const [totalRowsCount, setTotalRowsCount] = useState(0);

  const [confirmingJobId, setConfirmingJobId] = useState<number | null>(null);
  const [confirmProgress, setConfirmProgress] = useState<{ total: number; done: number; failed: number } | null>(null);

  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [tableWidth, setTableWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (tableRef.current) {
        setTableWidth(tableRef.current.scrollWidth);
      }
    };
    updateWidth();
    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(updateWidth, 100);
    window.addEventListener('resize', updateWidth);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateWidth);
    };
  }, [rows, showOnlyErrors]);

  const handleTopScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (bottomScrollRef.current && bottomScrollRef.current.scrollLeft !== e.currentTarget.scrollLeft) {
      bottomScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleBottomScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (topScrollRef.current && topScrollRef.current.scrollLeft !== e.currentTarget.scrollLeft) {
      topScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const loadJobs = async (page = 1) => {
    setLoadingJobs(true);
    try {
      const resp = await client.get(
        `/search/admin/import/?status=pending_review&page=${page}`,
      );
      setJobs(resp.data.results || resp.data);
      setTotalJobsCount(resp.data.count || (Array.isArray(resp.data) ? resp.data.length : 0));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => {
    loadJobs(currentPage);
  }, [currentPage]);

  const selectJob = async (job: ImportJob, onlyErrors = showOnlyErrors, page = 1) => {
    setSelectedJob(job);
    setLoadingRows(true);
    try {
      const resp = await client.get(
        `/search/admin/import/${job.id}/rows/?page=${page}&page_size=100${onlyErrors ? '&only_errors=true' : ''}`,
      );
      const data = resp.data.results || [];
      setRows(data);
      setTotalRowsCount(resp.data.count || 0);

      const summary: Record<string, number> = {};
      data.forEach((row: any) => {
        if (!row.is_valid && row.errors) {
          Object.keys(row.errors).forEach((col) => {
            summary[col] = (summary[col] || 0) + 1;
          });
        }
      });
      setMissingSummary(summary);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRows(false);
    }
  };

  useEffect(() => {
    if (selectedJob) {
      selectJob(selectedJob, showOnlyErrors, rowsPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnlyErrors, rowsPage]);

  const handleConfirm = async () => {
    if (!selectedJob) return;
    try {
      const jobId = selectedJob.id;
      await client.post(`/search/admin/import/${jobId}/confirm/`, {
        force: true,
      });
      
      setConfirmingJobId(jobId);
      setConfirmProgress({ total: selectedJob.valid_rows, done: 0, failed: 0 });
      
      const interval = setInterval(async () => {
        try {
          const resp = await client.get(`/search/admin/import/${jobId}/`);
          const jobData = resp.data;
          
          if (jobData.status === 'completed' || jobData.status === 'failed') {
            clearInterval(interval);
            setConfirmingJobId(null);
            
            if (jobData.status === 'completed') {
              showInfo({
                title: "Confirmed",
                message: "Valid rows have been added as drafts. Job completed.",
              });
            } else {
              showInfo({
                title: "Failed",
                message: "Import failed during processing.",
                variant: "warning"
              });
            }
            
            setJobs((prev) => prev.filter((j) => j.id !== jobId));
            setSelectedJob((prev) => prev?.id === jobId ? null : prev);
            loadJobs(currentPage);
          } else {
            setConfirmProgress({
              total: jobData.valid_rows,
              done: jobData.inserted_rows || 0,
              failed: jobData.failed_rows || 0
            });
          }
        } catch(e) {
          console.error("Polling error", e);
        }
      }, 1000);

    } catch (e: any) {
      console.error(e);
      const msg = e.response?.data?.detail || "Failed to confirm import";
      showInfo({ title: "Error", message: msg, variant: "warning" });
    }
  };

  const handleDelete = async (e: React.MouseEvent, job: ImportJob) => {
    e.stopPropagation();
    try {
      await client.delete(`/search/admin/import/${job.id}/`);
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
      if (selectedJob?.id === job.id) setSelectedJob(null);
      loadJobs(currentPage);
      showInfo({ title: "Deleted", message: "Import job deleted." });
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownloadErrors = async () => {
    if (!selectedJob) return;
    try {
      const response = await client.get(
        `/search/admin/import/${selectedJob.id}/download-errors/`,
        {
          responseType: "blob",
        },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `import_errors_job_${selectedJob.id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Failed to download errors", error);
      showInfo({
        title: "Error",
        message: "Failed to download error sheet.",
        variant: "warning",
      });
    }
  };

  const handleCellSave = async (rowId: number, col: string, val: string) => {
    if (!selectedJob) return;
    try {
      const resp = await client.patch(`/search/admin/import/${selectedJob.id}/rows/${rowId}/`, {
        data: { [col]: val }
      });
      // Update local row with response
      setRows((prev) => prev.map((r) => (r.id === rowId ? resp.data : r)));
      // Also silently reload the job to get updated counts
      const jobResp = await client.get(`/search/admin/import/${selectedJob.id}/`);
      setSelectedJob(jobResp.data);
      setJobs((prev) => prev.map((j) => (j.id === selectedJob.id ? jobResp.data : j)));
    } catch (err: any) {
      console.error("Failed to update cell", err);
      showInfo({
        title: "Update Failed",
        message: err.response?.data?.detail || "Could not save changes.",
        variant: "warning"
      });
    }
  };

  const renderTable = () => {
    if (loadingRows)
      return (
        <div className="p-8 flex justify-center">
          <LoadingSpinner />
        </div>
      );
    if (!rows.length)
      return <div className="p-8 text-gray-500">No rows found.</div>;

    const allCols = Object.keys(rows[0].data || {});

    return (
      <div className="flex flex-col border border-gray-200 rounded-lg overflow-hidden">
        <div 
          ref={topScrollRef}
          className="overflow-x-auto overflow-y-hidden bg-gray-50 border-b border-gray-200"
          onScroll={handleTopScroll}
        >
          <div style={{ width: tableWidth ? `${tableWidth}px` : '100%', height: '1px' }}></div>
        </div>
        
        <div 
          ref={bottomScrollRef}
          className="overflow-x-auto bg-white"
          onScroll={handleBottomScroll}
        >
          <table ref={tableRef} className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-gray-900 bg-gray-50 sticky top-0 z-10 w-16">
                Row
              </th>
              {allCols.map((c) => (
                <th
                  key={c}
                  className="px-3 py-2 text-left font-semibold text-gray-900 bg-gray-50 sticky top-0 z-10 whitespace-nowrap"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {rows.map((row) => (
              <tr key={row.id} className={!row.is_valid ? "bg-red-50" : ""}>
                <td className="px-3 py-2 text-gray-500 font-mono text-xs">
                  {row.row_index + 1}
                </td>
                {allCols.map((c) => {
                  const hasError = !row.is_valid && row.errors && row.errors[c];
                  const val = row.data[c] ?? "";
                  return (
                    <td
                      key={c}
                      className={`px-3 py-2 whitespace-nowrap overflow-hidden max-w-[200px] text-ellipsis ${hasError ? "text-red-700 font-medium" : "text-gray-900"} cursor-text`}
                      title={hasError ? String(row.errors[c]) : undefined}
                    >
                      <InlineEditor
                        value={String(val)}
                        onSave={(newVal) => {
                          if (newVal !== String(val)) {
                            handleCellSave(row.id, c, newVal);
                          }
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    );
  };

  const renderRowsPagination = () => {
    if (totalRowsCount === 0) return null;
    const totalPages = Math.ceil(totalRowsCount / 100);
    if (totalPages <= 1) return null;

    const currentBlock = Math.floor((rowsPage - 1) / 10);
    const startPage = currentBlock * 10 + 1;
    const endPage = Math.min(startPage + 9, totalPages);

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center gap-4">
        <span className="text-xs text-gray-500">Page {rowsPage} of {totalPages} (Total: {totalRowsCount})</span>
        <div className="flex items-center gap-1">
          {startPage > 1 && (
            <button
              onClick={() => setRowsPage(startPage - 1)}
              className="px-2 py-1 text-xs bg-white border border-gray-300 hover:bg-gray-100 rounded"
            >
              &lt;
            </button>
          )}
          {pages.map((p) => (
            <button
              key={p}
              onClick={() => setRowsPage(p)}
              className={`px-2 py-1 text-xs rounded border ${
                rowsPage === p
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white border-gray-300 hover:bg-gray-100"
              }`}
            >
              {p}
            </button>
          ))}
          {endPage < totalPages && (
            <button
              onClick={() => setRowsPage(endPage + 1)}
              className="px-2 py-1 text-xs bg-white border border-gray-300 hover:bg-gray-100 rounded"
            >
              &gt;
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderPagination = () => {
    if (totalJobsCount === 0) return null;
    const totalPages = Math.ceil(totalJobsCount / 5);
    if (totalPages <= 1) return null;

    const currentBlock = Math.floor((currentPage - 1) / 10);
    const startPage = currentBlock * 10 + 1;
    const endPage = Math.min(startPage + 9, totalPages);

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex justify-center items-center gap-1 mt-4 mb-2 shrink-0">
        {startPage > 1 && (
          <button
            onClick={() => setCurrentPage(startPage - 1)}
            className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
          >
            &lt;
          </button>
        )}
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => setCurrentPage(p)}
            className={`px-2 py-1 text-sm rounded ${
              currentPage === p
                ? "bg-blue-600 text-white"
                : "bg-gray-100 hover:bg-gray-200"
            }`}
          >
            {p}
          </button>
        ))}
        {endPage < totalPages && (
          <button
            onClick={() => setCurrentPage(endPage + 1)}
            className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
          >
            &gt;
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col md:flex-row gap-6">
      <div className="w-full md:w-1/3 flex flex-col gap-4 overflow-y-auto max-h-[calc(100vh-16rem)] pr-2">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 shrink-0">
          Pending Uploads
        </h3>
        {loadingJobs ? (
          <div className="flex justify-center p-4">
            <LoadingSpinner />
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-sm text-gray-500 bg-white p-6 rounded-lg border border-gray-200 shadow-sm text-center">
            No pending uploads.
          </div>
        ) : (
          jobs.map((job) => (
            <div
              key={job.id}
              onClick={() => selectJob(job)}
              className={`p-4 bg-white rounded-lg border shadow-sm cursor-pointer transition-colors relative ${selectedJob?.id === job.id ? "border-blue-500 ring-1 ring-blue-500" : "border-gray-200 hover:border-blue-300"}`}
            >
              <div className="flex justify-between items-start mb-2">
                <span
                  className="font-semibold text-gray-900 truncate pr-6 text-sm"
                  title={job.filename}
                >
                  {job.filename}
                </span>
                <button
                  onClick={(e) => handleDelete(e, job)}
                  className="text-gray-400 hover:text-red-600 absolute right-4 top-4"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="text-xs text-gray-500 mb-2">
                Model: {job.target_model}
              </div>
              <div className="flex gap-4 text-sm mt-3">
                <div className="flex flex-col">
                  <span className="text-gray-500 text-xs uppercase">Total</span>
                  <span className="font-medium text-gray-900">
                    {job.total_rows}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-gray-500 text-xs uppercase">
                    Errors
                  </span>
                  <span
                    className={`font-medium ${job.invalid_rows > 0 ? "text-red-600" : "text-green-600"}`}
                  >
                    {job.invalid_rows}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
        {renderPagination()}
      </div>

      <div className="w-full md:w-2/3">
        {selectedJob ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col h-[calc(100vh-16rem)] min-h-[500px]">
            <div className="p-5 border-b border-gray-200 flex justify-between items-start flex-shrink-0 bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {selectedJob.filename}
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedJob.invalid_rows > 0
                    ? "Review the validation errors below."
                    : "All rows are valid and ready to be imported."}
                </p>
              </div>
              <div className="flex gap-2">
                {selectedJob.invalid_rows > 0 && (
                  <button
                    onClick={handleDownloadErrors}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 shadow-sm"
                  >
                    Download Error Sheet
                  </button>
                )}
                <button
                  onClick={handleConfirm}
                  disabled={selectedJob.valid_rows === 0}
                  className={`px-3 py-1.5 text-sm font-medium text-white rounded-md shadow-sm ${selectedJob.valid_rows === 0 ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                >
                  Confirm Valid Rows ({selectedJob.valid_rows})
                </button>
              </div>
            </div>

            <div className="px-5 py-2 border-b border-gray-200 bg-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={showOnlyErrors}
                    onChange={(e) => {
                      setRowsPage(1);
                      setShowOnlyErrors(e.target.checked);
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  Show Only Errors
                </label>
                <div className="text-xs text-gray-500 italic hidden sm:block">
                  Click any cell to edit its value directly.
                </div>
              </div>
              {renderRowsPagination()}
            </div>

            <div className="p-5 flex-1 overflow-y-auto">
              {Object.keys(missingSummary).length > 0 && (
                <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Validation Errors (Current Page)
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <ul className="list-disc pl-5 space-y-1">
                          {Object.entries(missingSummary).map(
                            ([col, count]) => (
                              <li key={col}>
                                <strong>{col}</strong>: {count} rows
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {renderTable()}
            </div>
          </div>
        ) : (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center h-full min-h-[300px] text-gray-500">
            Select an upload from the left to view details
          </div>
        )}
      </div>

      {confirmingJobId && confirmProgress && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-full shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Processing Import...</h3>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(100, Math.max(0, ((confirmProgress.done + confirmProgress.failed) / (confirmProgress.total || 1)) * 100))}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>{confirmProgress.done + confirmProgress.failed} / {confirmProgress.total} processed</span>
              {confirmProgress.failed > 0 && <span className="text-red-500">{confirmProgress.failed} failed</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyUploadsTab;

const InlineEditor: React.FC<{ value: string; onSave: (v: string) => void }> = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);

  useEffect(() => {
    setVal(value);
  }, [value]);

  if (!editing) {
    return (
      <div 
        onClick={() => setEditing(true)} 
        className="w-full min-h-[1.5rem] hover:bg-gray-100 hover:ring-1 ring-gray-300 rounded px-1 -ml-1 transition-colors"
      >
        {val || <span className="text-gray-300 italic">empty</span>}
      </div>
    );
  }

  return (
    <input
      autoFocus
      type="text"
      className="w-full px-1 py-0.5 border border-blue-500 rounded text-gray-900 shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 -ml-1"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        setEditing(false);
        onSave(val);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          setEditing(false);
          onSave(val);
        } else if (e.key === "Escape") {
          setVal(value);
          setEditing(false);
        }
      }}
    />
  );
};

